import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  SendButton,
  useMessageComposer,
  useMessageComposerHasSendableData,
  useStateStore,
} from "stream-chat-react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ScheduleMessageMenu, ScheduleTimeModal } from "./ScheduleMessageMenu";
import { scheduledMessageErrorText, useScheduledMessages } from "./ScheduledMessagesContext";
import {
  draftClientMessageId,
  formatScheduledTime,
  getChannelDeadline,
  getScheduleWindowError,
  restoreComposerKeepingNewDraft,
  takeComposerSnapshot,
  toSchedulePayload,
} from "./scheduledMessageUtils";

const composerStateSelector = (state) => ({
  editedMessage: state.editedMessage,
  pollId: state.pollId,
});
const textStateSelector = (state) => ({ command: state.command });
const attachmentStateSelector = (state) => ({ attachments: state.attachments });

const isUploading = (attachment) =>
  ["uploading", "pending"].includes(attachment?.localMetadata?.uploadState);

// Stream's send button plus Slack's caret: "Schedule message" for later.
// Passed to <Channel> as SendButton, so thread replies get it too. Editing a
// sent message, polls, slash commands and in-flight uploads send right away
// only.
export default function ScheduleSendButton({ sendMessage, ...rest }) {
  const messageComposer = useMessageComposer();
  const hasSendableData = useMessageComposerHasSendableData();
  const scheduled = useScheduledMessages();
  const { editedMessage, pollId } = useStateStore(messageComposer.state, composerStateSelector);
  const { command } = useStateStore(messageComposer.textComposer.state, textStateSelector);
  const { attachments } = useStateStore(
    messageComposer.attachmentManager.state,
    attachmentStateSelector
  );
  const channelDeadline = getChannelDeadline(messageComposer.channel);
  const draftId = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const canSchedule =
    Boolean(scheduled) &&
    hasSendableData &&
    !editedMessage &&
    !pollId &&
    !command &&
    !(attachments || []).some(isUploading);

  const scheduleAt = async (sendAt) => {
    if (scheduling) return;
    const composition = await messageComposer.compose();
    if (!composition?.message) return;
    const message = toSchedulePayload(composition.message);
    const windowError = getScheduleWindowError(
      sendAt,
      message.attachments,
      new Date(),
      channelDeadline
    );
    if (windowError) throw new Error(windowError);
    const channel = messageComposer.channel;
    const composerId = messageComposer.state.getLatestValue().id;
    // Everything the message stores is part of the draft's identity, so a
    // changed attachment, quote or mention is a new draft, not a retry.
    const clientMessageId = draftClientMessageId(draftId, `${composerId}|${JSON.stringify(message)}`);
    // Clear now, restore on failure: while the request is in flight there's
    // nothing for Send or Enter to post a second copy of.
    const restoreComposer = takeComposerSnapshot(messageComposer);
    setScheduling(true);
    try {
      // Drafts are on (StreamChatWrapper): drop the draft before clearing, or
      // the scheduled text comes back the next time the channel opens.
      await messageComposer.deleteDraft().catch(() => {});
      messageComposer.clear();
      const row = await scheduled.schedule({
        channelType: channel.type,
        channelId: channel.id,
        sendAt,
        message,
        clientMessageId,
      });
      // Done with this draft: the next one gets a fresh id even if it reads
      // the same.
      draftId.current = null;
      // A retry of a request that already went through returns the original
      // row, so confirm the time that was actually saved.
      toast.success(`Scheduled for ${formatScheduledTime(row?.send_at || sendAt)}`, {
        autoClose: 3000,
      });
    } catch (error) {
      const reason = scheduledMessageErrorText(
        error,
        "We couldn't schedule this message. Please try again."
      );
      // Put the message back. If the user started a new one meanwhile, keep
      // that too, after it, so neither is lost.
      if (messageComposer.compositionIsEmpty) {
        restoreComposer();
        throw new Error(reason);
      }
      restoreComposerKeepingNewDraft(messageComposer, restoreComposer);
      throw new Error(`${reason} It's back in the message box, above what you'd started typing.`);
    } finally {
      setScheduling(false);
    }
  };

  const scheduleFromPreset = (sendAt) => {
    scheduleAt(sendAt).catch((error) => toast.error(error.message, { autoClose: 4000 }));
  };

  return (
    <div className="flex items-center">
      <SendButton sendMessage={sendMessage} {...rest} />
      {scheduled && !editedMessage && (
        <>
          <button
            type="button"
            aria-label="Schedule message"
            title="Schedule for later"
            disabled={!canSchedule || scheduling}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            className="ml-0.5 flex h-8 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ExpandLessIcon fontSize="small" />
          </button>
          <ScheduleMessageMenu
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
            onPick={scheduleFromPreset}
            onCustom={() => setCustomOpen(true)}
            attachments={attachments}
            channelDeadline={channelDeadline}
          />
          <ScheduleTimeModal
            open={customOpen}
            onClose={() => setCustomOpen(false)}
            onConfirm={({ sendAt }) => scheduleAt(sendAt)}
            attachments={attachments}
            channelDeadline={channelDeadline}
          />
        </>
      )}
    </div>
  );
}
